import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/utilities/ui'

const FieldLabel = ({ children, required }: { children: React.ReactNode; required?: boolean }) => (
  <p className="mb-2 block font-body text-sm leading-none font-semibold text-card-foreground">
    {children}
    {required && (
      <span className="required">
        * <span className="sr-only">(required)</span>
      </span>
    )}
  </p>
)

const SectionHeading = ({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) => (
  <h3 className={cn('font-family-body text-lg font-semibold text-foreground', className)}>
    {children}
  </h3>
)

const SectionSkeleton = ({ left, right }: { left: React.ReactNode; right: React.ReactNode }) => (
  <div className="grid w-full grid-cols-1 gap-8 border-t border-border pt-6 pb-8 md:grid-cols-2">
    <div className="md:pl-6">{left}</div>
    <div className="md:pr-6">{right}</div>
  </div>
)

export const EventsFormSkeleton = ({ formTitle = 'Submit an Event' }: { formTitle?: string }) => {
  return (
    <div className="relative flex h-full flex-col px-1 md:px-0" aria-busy="true">
      <div className="relative flex h-full flex-col border-t border-border pt-6 font-family-body">
        {/* Header */}
        <div className="grid w-full grid-cols-1 gap-8 pb-8 md:grid-cols-2">
          <h3 className="font-family-body text-2xl text-neutral-500 md:pl-6">Events</h3>
          <div>
            <h2 className="font-family-body text-2xl font-semibold text-foreground">{formTitle}</h2>
            <p className="mt-3 text-card-foreground">
              Share your community event with MAHA® Action. Submissions are reviewed before they
              appear on the site.
            </p>
          </div>
        </div>

        {/* Event Details */}
        <SectionSkeleton
          left={
            <div className="grid grid-cols-1 gap-6">
              <SectionHeading>Event Details</SectionHeading>
              <div>
                <FieldLabel required>Event Name</FieldLabel>
                <Skeleton className="h-12 w-full rounded-xl" />
              </div>
              <div>
                <FieldLabel required>Description</FieldLabel>
                <Skeleton className="h-40 w-full rounded-xl" />
              </div>
            </div>
          }
          right={
            <div>
              <FieldLabel required>Upload a display image for your event</FieldLabel>
              <Skeleton className="aspect-square w-full max-w-80 rounded-md" />
            </div>
          }
        />

        {/* Date & Time */}
        <SectionSkeleton
          left={
            <div>
              <SectionHeading className="pb-3">Date &amp; Time</SectionHeading>
              <p className="text-card-foreground">When does your event start and end?</p>
            </div>
          }
          right={<Skeleton className="h-24 w-full max-w-md rounded-xl" />}
        />

        {/* Capacity */}
        <SectionSkeleton
          left={
            <div>
              <SectionHeading className="pb-3">Capacity</SectionHeading>
              <p className="text-card-foreground">Optional. Leave blank for unlimited.</p>
            </div>
          }
          right={
            <div>
              <FieldLabel>Event Capacity</FieldLabel>
              <Skeleton className="h-12 w-full max-w-32 rounded-xl" />
            </div>
          }
        />

        {/* Topics */}
        <SectionSkeleton
          left={
            <div>
              <SectionHeading className="pb-3">Topics</SectionHeading>
              <p className="text-card-foreground">Select 1–3 topics that apply. (Required)</p>
            </div>
          }
          right={
            <div>
              <FieldLabel required>Topics</FieldLabel>
              <div className="flex flex-wrap gap-2">
                <Skeleton className="h-8 w-24 rounded-full" />
                <Skeleton className="h-8 w-32 rounded-full" />
                <Skeleton className="h-8 w-20 rounded-full" />
                <Skeleton className="h-8 w-28 rounded-full" />
                <Skeleton className="h-8 w-24 rounded-full" />
                <Skeleton className="h-8 w-36 rounded-full" />
              </div>
            </div>
          }
        />

        {/* Venue */}
        <SectionSkeleton
          left={
            <div className="grid grid-cols-1 gap-8">
              <div>
                <SectionHeading className="pb-3">Venue</SectionHeading>
                <p className="text-card-foreground">How will people attend your event?</p>
              </div>
              <div>
                <FieldLabel required>Event Format</FieldLabel>
                <div className="flex flex-col gap-3">
                  {['In person', 'Virtual', 'Hybrid (in person and virtual)'].map((option) => (
                    <div key={option} className="flex items-center gap-2">
                      <Skeleton className="size-4 rounded-full" />
                      <span className="font-body text-sm leading-none text-card-foreground">
                        {option}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          }
          right={
            <div className="flex max-w-lg flex-col gap-4">
              <div>
                <FieldLabel>Location</FieldLabel>
                <Skeleton className="h-12 w-full rounded-xl" />
              </div>
              <Skeleton className="h-40 w-full rounded-xl" />
            </div>
          }
        />

        {/* Consent & submit */}
        <SectionSkeleton
          left={<SectionHeading className="pb-3">Finish</SectionHeading>}
          right={
            <div className="grid grid-cols-1 gap-6">
              <div className="flex items-start gap-4">
                <Skeleton className="size-8 shrink-0 rounded-lg" />
                <p className="flex min-h-8 items-center font-body text-sm leading-5 font-semibold text-card-foreground">
                  I confirm the information above is accurate and I have authority to submit this
                  event.
                </p>
              </div>
              <Button type="button" className="w-full pr-0 md:pr-1" disabled>
                Submit Event
              </Button>
            </div>
          }
        />
      </div>
    </div>
  )
}
